import { LEADERBOARD_SIZE, THEORETICAL_MAX_SCORE } from "../constants";
import { isValidInitials, normalizeInitials } from "./initials";

export interface ScoreRecord {
  initials: string;
  score: number;
  submittedAt: number;
  key?: string;
}

export interface PublicScore {
  rank: number;
  initials: string;
  score: number;
}

export function isValidScoreValue(score: unknown): score is number {
  return (
    typeof score === "number" &&
    Number.isSafeInteger(score) &&
    score >= 0 &&
    score <= THEORETICAL_MAX_SCORE
  );
}

export function validateSubmission(body: unknown): { initials: string; score: number } | { error: string } {
  if (body === null || typeof body !== "object") {
    return { error: "Request body must be a JSON object." };
  }
  const payload = body as { initials?: unknown; score?: unknown };
  if (typeof payload.initials !== "string") {
    return { error: "Initials must be a string." };
  }
  const initials = normalizeInitials(payload.initials);
  if (!isValidInitials(initials)) {
    return { error: "Initials must be exactly three A–Z letters." };
  }
  if (!isValidScoreValue(payload.score)) {
    return { error: `Score must be an integer from 0 to ${THEORETICAL_MAX_SCORE}.` };
  }
  return { initials, score: payload.score };
}

export function sortScores(records: ScoreRecord[]): ScoreRecord[] {
  return [...records].sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.submittedAt - b.submittedAt;
  });
}

export function topScores(records: ScoreRecord[], limit = LEADERBOARD_SIZE): ScoreRecord[] {
  return sortScores(records).slice(0, limit);
}

export function toPublicScores(records: ScoreRecord[]): PublicScore[] {
  return topScores(records).map((record, index) => ({
    rank: index + 1,
    initials: record.initials,
    score: record.score,
  }));
}

export function qualifiesForLeaderboard(score: number, records: ScoreRecord[]): boolean {
  if (!isValidScoreValue(score)) {
    return false;
  }
  const ranked = topScores(records);
  if (ranked.length < LEADERBOARD_SIZE) {
    return true;
  }
  const last = ranked[ranked.length - 1];
  return last !== undefined && score >= last.score;
}

export function addScoreRecord(records: ScoreRecord[], incoming: ScoreRecord): ScoreRecord[] {
  if (records.length < LEADERBOARD_SIZE) {
    return topScores([...records, incoming]);
  }
  if (!qualifiesForLeaderboard(incoming.score, records)) {
    return topScores(records);
  }
  const ranked = topScores(records);
  const last = ranked[ranked.length - 1];
  const withoutLast = last
    ? records.filter(
        (record) =>
          !(
            record.initials === last.initials &&
            record.score === last.score &&
            record.submittedAt === last.submittedAt
          ),
      )
    : records;
  return topScores([...withoutLast, incoming]);
}

export function rankOfSubmission(
  submitted: ScoreRecord,
  records: ScoreRecord[],
): number | null {
  const ranked = topScores(records);
  const index = ranked.findIndex(
    (record) =>
      record.initials === submitted.initials &&
      record.score === submitted.score &&
      record.submittedAt === submitted.submittedAt,
  );
  return index >= 0 ? index + 1 : null;
}
